/**
 * V2 admin · Bank reconciliation review queue (COL-350).
 *
 * `finapi-sync-tick` imports bank transactions daily and `_shared/bank-reconcile.ts`
 * matches them to open rent receivables; anything it can't confidently apply
 * (partial amount, overpayment, unknown sender, duplicate, or several
 * candidate tenants) lands here as a `needs_review` row instead of being
 * silently applied. An admin either confirms the suggested match — which
 * marks the payment paid with the bank transaction attached as proof — or
 * rejects it with a reason.
 *
 * Two tabs: Open (unaddressed, oldest transfer first — the longest-waiting
 * money surfaces at the top) and Closed (decided history, newest decision
 * first, read-only audit trail).
 */
import { Fragment, useMemo, useRef, useState } from "react";
import { useScrollToTopOnChange } from "@/hooks/useScrollToTopOnChange";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ClipboardList,
  Landmark,
  SquarePen,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState } from "@/components/admin/v2/primitives/EmptyState";
import { TableSkeleton } from "@/components/admin/v2/primitives/TableSkeleton";
import { DebouncedSearchInput } from "@/components/admin/v2/primitives/DebouncedSearchInput";
import { Checkbox } from "@/components/ui/checkbox";
import { formatCents } from "@/lib/money";
import {
  decisionPayment,
  feePortionError,
  openBalanceCents,
  planTransferSplit,
  type BookedTransferSplit,
  periodLabel,
  splitSummary,
} from "@/lib/transfer-split";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  availableCents,
  candidateStillOpen,
  confirmBooksText,
  ConfirmAssignmentDialog,
  describeConfirmBlock,
  feeErrorMessage,
  feesOwedCents,
  isSimpleSplit,
  isTransferPortion,
  planConfirm,
} from "@/components/admin/v2/ReconciliationConfirmDialog";
import {
  useReconciliationReviewQueue,
  useReconciliationMutations,
  useOpenInvoices,
  useTransferPaidMonths,
  type OpenInvoice,
  type ReconciliationMatchRow,
} from "@/hooks/v2/useReconciliation";

type Translate = (key: string, fallback?: string) => string;

/** A needs_review row holding what an earlier decision left unapplied. */
function isRemainderRow(row: ReconciliationMatchRow): boolean {
  return (
    row.status === "needs_review" &&
    row.payment_id == null &&
    isTransferPortion(row)
  );
}

/** "" → null (let the split rule decide); otherwise the admin-stated cents. */
function parseFeeInput(value: string | undefined): number | null {
  const trimmed = (value ?? "").trim();
  if (trimmed === "") return null;
  return Math.max(
    0,
    Math.round((parseFloat(trimmed.replace(",", ".")) || 0) * 100)
  );
}

function skeletonColumns(t: Translate) {
  return [
    {
      label: t("admin.reconciliation.transaction", "Transaction"),
      bar: "w-40",
    },
    {
      label: t("admin.reconciliation.suggestedMatch", "Suggested match"),
      bar: "w-40",
    },
    {
      label: t(
        "admin.reconciliation.needsReviewBecause",
        "Needs review because"
      ),
      bar: "w-32",
    },
    {
      label: t("admin.reconciliation.action", "Action"),
      className: "text-right",
      bar: "w-20",
    },
  ];
}

function methodLabel(method: string, t: Translate): string {
  switch (method) {
    case "mandate_iban":
      return t("admin.reconciliation.method.mandateIban", "IBAN on mandate");
    case "tenant_name":
      return t("admin.reconciliation.method.tenantName", "tenant name");
    case "amount_due_month":
      return t(
        "admin.reconciliation.method.amountDueMonth",
        "amount + due month"
      );
    case "reference":
      return t("admin.reconciliation.method.reference", "payment reference");
    case "manual":
      return t("admin.reconciliation.method.manual", "manual");
    default:
      return method;
  }
}

function statusLabel(status: string, t: Translate): string {
  switch (status) {
    case "confirmed":
      return t("admin.reconciliation.status.confirmed", "Confirmed");
    case "rejected":
      return t("admin.reconciliation.status.rejected", "Rejected");
    case "auto_matched":
      return t("admin.reconciliation.status.autoMatched", "Auto-matched");
    default:
      return status;
  }
}

function fmtDate(dateish: string | null | undefined, language: string): string {
  if (!dateish) return "—";
  const d = new Date(`${String(dateish).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function TransactionCell({ row }: { row: ReconciliationMatchRow }) {
  const { t, language } = useLanguage();
  const tx = row.bank_transactions;
  if (!tx) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="space-y-0.5">
      <div className="font-medium">
        {tx.counterpart_name ||
          t("admin.reconciliation.unknownSender", "Unknown sender")}
      </div>
      <div className="text-xs text-muted-foreground font-mono">
        {tx.counterpart_iban || t("admin.reconciliation.noIban", "No IBAN")}
      </div>
      <div className="text-xs text-muted-foreground">
        {fmtDate(tx.booking_date ?? tx.value_date, language)} ·{" "}
        {formatCents(availableCents(row), tx.currency)}
        {/* COL-508: a remainder row, or a decision that booked only part of
            the transfer, names the whole it came from. */}
        {isTransferPortion(row) && (
          <>
            {" "}
            {(row.status === "needs_review"
              ? t(
                  "admin.reconciliation.split.leftOfTransfer",
                  "left from a transfer of {total}"
                )
              : t(
                  "admin.reconciliation.split.ofTransfer",
                  "booked from a transfer of {total}"
                )
            ).replace("{total}", formatCents(tx.amount_cents, tx.currency))}
          </>
        )}
      </div>
      {tx.purpose && (
        <div
          className="text-xs text-muted-foreground/80 max-w-xs truncate"
          title={t(
            "admin.reconciliation.paymentReferenceTitle",
            "Payment reference (Verwendungszweck): {purpose}"
          ).replace("{purpose}", tx.purpose)}
        >
          <span className="font-medium">
            {t("admin.reconciliation.refLabel", "Ref:")}
          </span>{" "}
          {tx.purpose}
        </div>
      )}
    </div>
  );
}

/** Deep link into the tenant page — route needs property/room/booking ids,
 * all of which ride along on the candidate payment's join. */
function tenantHref(p: ReconciliationMatchRow["payments"]): string | null {
  const booking = p?.bookings;
  const room = booking?.rooms;
  const prop = room?.properties;
  if (!booking?.id || !room?.id || !prop?.id) return null;
  return `/admin/properties/${prop.id}/rooms/${room.id}/tenants/${booking.id}`;
}

function SuggestedMatchCell({ row }: { row: ReconciliationMatchRow }) {
  const { t, language } = useLanguage();
  // COL-508: on a decided row this is the invoice the decision actually booked
  // against, which `payment_id` cannot always be — see `decisionPayment`.
  const p = decisionPayment(row);
  if (!p)
    return (
      <span className="text-muted-foreground">
        {t("admin.reconciliation.noCandidate", "No candidate")}
      </span>
    );
  const room = p.bookings?.rooms;
  const prop = room?.properties;
  const href = tenantHref(p);
  const name = row.tenantName || row.tenantEmail || "—";
  return (
    <div className="space-y-0.5">
      <div className="font-medium">
        {href ? (
          <Link
            to={href}
            className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
          >
            {name}
          </Link>
        ) : (
          name
        )}
      </div>
      <div className="text-xs text-muted-foreground">
        {prop?.title || "—"}
        {room?.title ? ` · ${room.title}` : ""}
      </div>
      <div className="text-xs text-muted-foreground">
        {periodLabel(p.due_date, language)} ·{" "}
        {formatCents(p.amount_cents, p.currency)}
      </div>
      {/* COL-417: what a transfer is actually measured against. Showing only
          the billed amount is what made a second transfer for a part-paid
          month look like a mismatch. */}
      {(p.received_cents ?? 0) > 0 && (
        <div className="text-xs font-medium text-blue-700">
          {formatCents(openBalanceCents(p), p.currency)}{" "}
          {t("admin.reconciliation.stillOpen", "still open")} ·{" "}
          {formatCents(p.received_cents ?? 0, p.currency)}{" "}
          {t("admin.reconciliation.alreadyReceived", "already received")}
        </div>
      )}
      {row.status === "needs_review" && !candidateStillOpen(p) && (
        <Badge
          className="bg-slate-100 text-slate-600 hover:bg-slate-100"
          variant="secondary"
          title={t(
            "admin.reconciliation.invoiceSettledTitle",
            "This invoice was settled after the suggestion was made (another transfer, SEPA, or a manual mark-paid) — use Assign to point the transaction at a different open invoice."
          )}
        >
          {t(
            "admin.reconciliation.invoiceAlreadyStatus",
            "Invoice already {status}"
          ).replace(
            "{status}",
            p.status === "paid"
              ? t("admin.reconciliation.paid", "paid")
              : p.status
          )}
        </Badge>
      )}
      {row.match_method && (
        <div className="text-xs text-muted-foreground/80">
          {t("admin.reconciliation.matchedVia", "matched via {method}").replace(
            "{method}",
            methodLabel(row.match_method, t)
          )}
          {row.confidence != null
            ? ` · ${Math.round(row.confidence * 100)}%`
            : ""}
        </div>
      )}
    </div>
  );
}

/** The admin-entered part of the decision trail — the note typed on assign
 * ("[assigned by admin … : NOTE]") or the reason typed on reject
 * ("rejected: NOTE"). Engine reasoning and auto-reject markers are NOT notes
 * (Neel #3A: the clipboard shows only what a human wrote). */
export function adminDecisionNote(reason: string | null): string | null {
  if (!reason) return null;
  const assign = /\[assigned by admin[^\]]*?→ [0-9a-f-]+: ([^\]]+)\]/.exec(
    reason
  );
  if (assign) return assign[1].trim();
  const rejected = /(?:^|\| )rejected: (.+)$/.exec(reason);
  if (rejected) return rejected[1].trim();
  return null;
}

function OutcomeCell({ row }: { row: ReconciliationMatchRow }) {
  const { t, language } = useLanguage();
  const tone =
    row.status === "rejected"
      ? "bg-rose-100 text-rose-700 hover:bg-rose-100"
      : "bg-emerald-100 text-emerald-700 hover:bg-emerald-100";
  const note = adminDecisionNote(row.review_reason);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-end gap-1">
        {note && (
          // Neel #3B: the note the admin wrote when deciding, on a click —
          // engine reasoning stays in the Reason column, not in here.
          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6"
                aria-label={t(
                  "admin.reconciliation.showDecisionNote",
                  "Show decision note"
                )}
                title={t(
                  "admin.reconciliation.showDecisionNote",
                  "Show decision note"
                )}
              >
                <ClipboardList className="h-3.5 w-3.5" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="max-w-sm text-xs whitespace-pre-wrap"
            >
              {note}
            </PopoverContent>
          </Popover>
        )}
        <Badge className={tone} variant="secondary">
          {statusLabel(row.status, t)}
        </Badge>
      </div>
      <div className="text-xs text-muted-foreground text-right">
        {fmtDate(row.decided_at, language)}
      </div>
    </div>
  );
}

function rowMatchesSearch(
  row: ReconciliationMatchRow,
  needle: string
): boolean {
  const tx = row.bank_transactions;
  const p = decisionPayment(row);
  const hay = [
    tx?.counterpart_name,
    tx?.counterpart_iban,
    tx?.purpose,
    tx ? formatCents(tx.amount_cents, tx.currency) : null,
    tx ? String(tx.amount_cents / 100) : null,
    // COL-508: a remainder row is found by the amount it still holds too.
    tx && isTransferPortion(row)
      ? formatCents(availableCents(row), tx.currency)
      : null,
    row.tenantName,
    row.tenantEmail,
    p?.bookings?.rooms?.title,
    p?.bookings?.rooms?.properties?.title,
    row.review_reason,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return hay.includes(needle);
}

/** One selectable invoice line inside the assign picker. */
function invoiceParts(inv: OpenInvoice, t: Translate, language: string) {
  const room = inv.bookings?.rooms;
  const prop = room?.properties;
  const where = [prop?.title, room?.title].filter(Boolean).join(" · ");
  const tenant =
    inv.tenantName ||
    inv.tenantEmail ||
    t("admin.reconciliation.unknownTenant", "Unknown tenant");
  // A deposit is one fixed invoice, not a month — label it as such instead of a period.
  const kind =
    inv.type === "deposit"
      ? t("admin.tenants.securityDeposit", "Security deposit")
      : periodLabel(inv.due_date, language);
  return { tenant, where: where || "—", kind };
}

function invoiceLabel(
  inv: OpenInvoice,
  t: Translate,
  language: string
): string {
  const { tenant, where, kind } = invoiceParts(inv, t, language);
  return `${tenant} — ${where} — ${kind} · ${formatCents(inv.amount_cents, inv.currency)}`;
}

function invoiceMatchesSearch(
  inv: OpenInvoice,
  needle: string,
  t: Translate,
  language: string
): boolean {
  return invoiceLabel(inv, t, language).toLowerCase().includes(needle);
}

export default function ReconciliationReviewPage() {
  const { t, language } = useLanguage();
  const { data: rows = [], isLoading } = useReconciliationReviewQueue();
  const { confirm, reject, assign } = useReconciliationMutations();
  const [tab, setTab] = useState<"open" | "closed">("open");
  useScrollToTopOnChange(tab);
  const [search, setSearch] = useState("");
  const [rejectTarget, setRejectTarget] =
    useState<ReconciliationMatchRow | null>(null);
  const [reasonDraft, setReasonDraft] = useState("");
  // The row whose Confirm was clicked: nothing is booked until the admin
  // confirms again in the dialog (fat-finger guard).
  const [confirmTargetId, setConfirmTargetId] = useState<string | null>(null);
  const [assignTarget, setAssignTarget] =
    useState<ReconciliationMatchRow | null>(null);
  const [assignSearch, setAssignSearch] = useState("");
  const [assignInvoiceId, setAssignInvoiceId] = useState<string | null>(null);
  const [assignNote, setAssignNote] = useState("");
  // COL-417: Mahngebühr portion of the transfer being assigned — the same
  // role as `feeSplit` plays for Confirm, just scoped to the single dialog
  // target instead of keyed per row. COL-508: empty means "let the split
  // rule decide" (the oldest month's fees follow its rent).
  const [assignFeeAmount, setAssignFeeAmount] = useState("");
  // COL-508: whether a leftover after every open month becomes credit on the
  // next rent (default) or stays in the queue as its own row.
  const [assignCreditNext, setAssignCreditNext] = useState(true);
  // COL-637: the admin sets what the tenancy owes in Mahngebühren after this
  // transfer (e.g. settled for EUR 10 of EUR 25 → 0). Off by default; when on,
  // both an amount and a reason are required.
  const [feeAdjustOpen, setFeeAdjustOpen] = useState(false);
  const [feeAfterAmount, setFeeAfterAmount] = useState("");
  const [feeAdjustReason, setFeeAdjustReason] = useState("");

  const { openRows, closedRows } = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = (r: ReconciliationMatchRow) =>
      needle === "" || rowMatchesSearch(r, needle);
    // Open: the longest-waiting money first — sort by the transfer's bank
    // booking date, not the row's created_at (re-imports would reshuffle that).
    const open = rows
      .filter((r) => r.status === "needs_review")
      .filter(matches)
      .sort((a, b) =>
        String(a.bank_transactions?.booking_date ?? "9999").localeCompare(
          String(b.bank_transactions?.booking_date ?? "9999")
        )
      );
    // Closed: newest decision first (hook already orders it; keep stable).
    const closed = rows
      .filter((r) => r.status !== "needs_review")
      .filter(matches);
    return { openRows: open, closedRows: closed };
  }, [rows, search]);

  const visibleRows = tab === "open" ? openRows : closedRows;

  // COL-508: every open rent row — the picker's candidates AND the tenancy
  // view the split preview reads for Confirm, so it loads whenever there is
  // something on the Open tab to decide.
  const {
    data: openInvoices = [],
    isLoading: invoicesLoading,
    isError: invoicesFailed,
  } = useOpenInvoices(
    !!assignTarget || (tab === "open" && openRows.length > 0)
  );

  const assignCandidates = useMemo(() => {
    const needle = assignSearch.trim().toLowerCase();
    return needle === ""
      ? openInvoices
      : openInvoices.filter((inv) =>
          invoiceMatchesSearch(inv, needle, t, language)
        );
  }, [openInvoices, assignSearch, t, language]);

  const selectedInvoice = useMemo(
    () => openInvoices.find((inv) => inv.id === assignInvoiceId) ?? null,
    [openInvoices, assignInvoiceId]
  );
  // A deposit is one fixed invoice, not a tenancy of rent months: it books the
  // single invoice (server takes the COL-398 proof path), with no cascade, no
  // Mahngebühren and no carry-forward credit. The rent split, fee input and
  // credit toggle below all switch off for it.
  const isDepositAnchor = selectedInvoice?.type === "deposit";

  // COL-508: the split the dialog previews — the same rule the RPC applies,
  // computed here so the admin sees every month the transfer will touch
  // before submitting. The server's own split drives the toast.
  const assignAvailableCents = availableCents(assignTarget);
  const assignCurrency =
    assignTarget?.bank_transactions?.currency ??
    selectedInvoice?.currency ??
    "EUR";
  const assignFeesOwed = feesOwedCents(selectedInvoice);
  const assignFeeOverride = parseFeeInput(assignFeeAmount);
  const assignFeeError = selectedInvoice
    ? feePortionError(assignFeeOverride, assignAvailableCents, assignFeesOwed)
    : null;
  // COL-508: months this same transfer already paid — the RPC skips them, so
  // the preview must too. Only a remainder row can have any (see the hook).
  const { data: alreadyPaidMonths = [] } = useTransferPaidMonths(
    assignTarget?.bank_transaction_id ?? null,
    !!assignTarget && isTransferPortion(assignTarget)
  );

  const assignSplit = useMemo(
    () =>
      selectedInvoice && !isDepositAnchor
        ? planTransferSplit({
            availableCents: assignAvailableCents,
            anchor: selectedInvoice,
            rows: openInvoices,
            feesOwedCents: assignFeesOwed,
            feeOverrideCents: assignFeeError ? null : assignFeeOverride,
            creditNext: assignCreditNext,
            excludePaymentIds: alreadyPaidMonths,
          })
        : null,
    [
      selectedInvoice,
      isDepositAnchor,
      assignAvailableCents,
      openInvoices,
      assignFeesOwed,
      assignFeeOverride,
      assignFeeError,
      assignCreditNext,
      alreadyPaidMonths,
    ]
  );
  // Deposit: book up to its open balance, keep any overpayment as a remainder
  // row — mirrors the server branch so the preview matches what gets booked.
  const depositTakeCents =
    isDepositAnchor && selectedInvoice
      ? Math.min(assignAvailableCents, openBalanceCents(selectedInvoice))
      : 0;
  const depositRemainderCents = isDepositAnchor
    ? Math.max(0, assignAvailableCents - depositTakeCents)
    : 0;
  // What is left after every open month — independent of where the toggle
  // sends it, so the toggle does not disappear the moment it is switched off.
  const assignLeftoverCents = assignSplit
    ? assignSplit.credit_cents + assignSplit.remainder_cents
    : 0;
  // COL-637: Mahngebühren owed after this transfer — the split's own result,
  // or the amount the admin set.
  const feeAdjustActive =
    feeAdjustOpen && !isDepositAnchor && assignFeesOwed > 0;
  const feesAfterSplitCents = assignSplit
    ? assignSplit.fees_owed_after_cents
    : assignFeesOwed;
  const feeAfterOverride = feeAdjustActive
    ? parseFeeInput(feeAfterAmount)
    : null;
  const feeAdjustInvalid =
    feeAdjustActive &&
    (feeAfterOverride == null || feeAdjustReason.trim() === "");
  const feesAfterCents = feeAfterOverride ?? feesAfterSplitCents;
  // The Mahngebühren row of the booking table — booked right after the oldest
  // month, so it renders there.
  const feeRow = assignSplit ? (
    <div className="contents" data-testid="split-row">
      <span className="min-w-0 hyphens-auto break-words">
        {t("admin.reconciliation.split.feeLine", "Mahngebühren")}
      </span>
      <span className="text-right text-muted-foreground">
        {formatCents(assignFeesOwed, assignCurrency)}
      </span>
      <span className="text-right font-medium">
        {formatCents(assignSplit.fee_cents, assignCurrency)}
      </span>
      <span
        className={
          feesAfterCents === 0
            ? "text-right text-emerald-700"
            : "text-right text-blue-700"
        }
        data-testid="assign-fees-after"
      >
        {feesAfterCents === 0
          ? t("admin.reconciliation.split.feesCleared", "cleared")
          : t(
              "admin.reconciliation.split.stillOpen",
              "{amount} still open"
            ).replace("{amount}", formatCents(feesAfterCents, assignCurrency))}
        {feeAfterOverride != null &&
          feeAfterOverride !== feesAfterSplitCents && (
            <span className="block text-xs text-amber-700">
              {t(
                "admin.reconciliation.split.feesAdjusted",
                "adjusted from {amount}"
              ).replace(
                "{amount}",
                formatCents(feesAfterSplitCents, assignCurrency)
              )}
            </span>
          )}
      </span>
    </div>
  ) : null;

  // Per-row Mahngebühr portion of a transfer, keyed by match id (COL-417).
  const [feeSplit, setFeeSplit] = useState<Record<string, string>>({});

  /** The split one-click Confirm would book for a queue row (credit on). */
  const confirmSplitFor = (row: ReconciliationMatchRow) =>
    planConfirm(row, openInvoices, parseFeeInput(feeSplit[row.id]));

  const toastBooked = (
    booked: BookedTransferSplit | null,
    currency: string
  ) => {
    // An RPC that returned nothing to read (should not happen once the
    // migration is live) still succeeded — say so without inventing a split.
    const summary = booked ? splitSummary(booked, currency, t, language) : "";
    toast.success(
      summary
        ? t(
            "admin.reconciliation.split.toastApplied",
            "Transfer applied — {summary}"
          ).replace("{summary}", summary)
        : t("admin.reconciliation.split.toastAppliedPlain", "Transfer applied.")
    );
  };

  const confirmMatch = async (
    row: ReconciliationMatchRow,
    feeCents: number | null
  ): Promise<boolean> => {
    try {
      const booked = await confirm.mutateAsync({
        matchId: row.id,
        feeCents,
        creditNext: true,
      });
      toastBooked(
        booked,
        row.bank_transactions?.currency ??
          decisionPayment(row)?.currency ??
          "EUR"
      );
      setFeeSplit((prev) => {
        const { [row.id]: _dropped, ...rest } = prev;
        return rest;
      });
      return true;
    } catch (err: any) {
      toast.error(
        err?.message ??
          t(
            "admin.reconciliation.toast.confirmFailed",
            "Could not confirm this match"
          )
      );
      return false;
    }
  };

  // Resolved from the live queue on every render, so the dialog always shows
  // what Confirm would book right now rather than a snapshot from the click:
  // a row another admin decided meanwhile drops out and closes the dialog.
  const confirmRow =
    rows.find((r) => r.id === confirmTargetId && r.status === "needs_review") ??
    null;
  const confirmPlan = confirmRow ? confirmSplitFor(confirmRow) : null;
  const confirmBlockedReason = confirmRow
    ? describeConfirmBlock(confirmRow, confirmPlan, invoicesFailed, t)
    : null;

  // `confirm.isPending` can lag a render behind a fast double-click; this
  // flips synchronously so a transfer is never submitted twice from the dialog.
  const confirmInFlight = useRef(false);
  const submitConfirm = async () => {
    if (!confirmRow || !confirmPlan || confirm.isPending) return;
    if (confirmInFlight.current) return;
    confirmInFlight.current = true;
    try {
      // Stay open on a failure (the error is toasted) so the admin can retry.
      if (await confirmMatch(confirmRow, confirmPlan.feeOverride))
        setConfirmTargetId(null);
    } finally {
      confirmInFlight.current = false;
    }
  };

  const openReject = (row: ReconciliationMatchRow) => {
    setReasonDraft("");
    setRejectTarget(row);
  };

  const openAssign = (row: ReconciliationMatchRow) => {
    setAssignSearch("");
    setAssignNote("");
    setAssignFeeAmount("");
    setAssignCreditNext(true);
    resetFeeAdjust();
    // Preselect the engine's candidate when it's still assignable.
    setAssignInvoiceId(
      row.payment_id && candidateStillOpen(row.payments) ? row.payment_id : null
    );
    setAssignTarget(row);
  };

  const resetFeeAdjust = () => {
    setFeeAdjustOpen(false);
    setFeeAfterAmount("");
    setFeeAdjustReason("");
  };

  const closeAssign = () => {
    setAssignTarget(null);
    setAssignSearch("");
    setAssignInvoiceId(null);
    setAssignNote("");
    setAssignFeeAmount("");
    setAssignCreditNext(true);
    resetFeeAdjust();
  };

  const submitAssign = async () => {
    if (!assignTarget || !assignInvoiceId || assignFeeError || feeAdjustInvalid)
      return;
    try {
      const booked = await assign.mutateAsync({
        matchId: assignTarget.id,
        paymentId: assignInvoiceId,
        note: assignNote.trim() || undefined,
        // A deposit ignores fee/credit server-side; send neutral values.
        feeCents: isDepositAnchor ? null : assignFeeOverride,
        creditNext: isDepositAnchor ? true : assignCreditNext,
        feeAdjustment:
          feeAdjustActive && feeAfterOverride != null
            ? {
                feesAfterCents: feeAfterOverride,
                reason: feeAdjustReason.trim(),
              }
            : null,
      });
      toastBooked(booked, assignCurrency);
      closeAssign();
    } catch (err: any) {
      // COL-637: the one adjustment refusal the dialog cannot foresee — the
      // transfer pays off all due rent, so the coverage rule clears the fees.
      if (String(err?.message ?? "").includes("fees_after_rent_covered")) {
        toast.error(
          t(
            "admin.reconciliation.feeAdjust.rentCovered",
            "This transfer pays all rent that is due, so the Mahngebühren are cleared automatically. Enter 0 or cancel the adjustment."
          )
        );
        return;
      }
      toast.error(
        err?.message ??
          t(
            "admin.reconciliation.toast.assignFailed",
            "Could not assign this transaction"
          )
      );
    }
  };

  const submitReject = async () => {
    if (!rejectTarget || !reasonDraft.trim()) return;
    try {
      await reject.mutateAsync({
        matchId: rejectTarget.id,
        reason: reasonDraft.trim(),
      });
      toast.success(
        t("admin.reconciliation.toast.matchRejected", "Match rejected.")
      );
      setRejectTarget(null);
      setReasonDraft("");
    } catch (err: any) {
      toast.error(
        err?.message ??
          t(
            "admin.reconciliation.toast.rejectFailed",
            "Could not reject this match"
          )
      );
    }
  };

  return (
    <AdminV2Layout
      title={t("admin.reconciliation.title", "Bank reconciliation")}
    >
      <p className="text-sm text-muted-foreground -mt-4 mb-4">
        {t(
          "admin.reconciliation.subtitle",
          "Reconcile bank transfers directly into the coliviq platform."
        )}
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Tabs value={tab} onValueChange={(v) => setTab(v as "open" | "closed")}>
          <TabsList>
            <TabsTrigger value="open">
              {t("admin.reconciliation.tab.open", "Open")}
              {openRows.length > 0 ? ` (${openRows.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="closed">
              {t("admin.reconciliation.tab.closed", "Closed")}
              {closedRows.length > 0 ? ` (${closedRows.length})` : ""}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <DebouncedSearchInput
          value={search}
          onDebouncedChange={setSearch}
          placeholder={t(
            "admin.reconciliation.searchPlaceholder",
            "Search sender, IBAN, reference, tenant, amount…"
          )}
          className="flex-1 min-w-56"
        />
      </div>

      {isLoading ? (
        <TableSkeleton columns={skeletonColumns(t)} />
      ) : visibleRows.length === 0 ? (
        <Card>
          <EmptyState
            icon={Landmark}
            title={
              search.trim()
                ? t(
                    "admin.reconciliation.empty.noMatchesTitle",
                    "No matches for this search"
                  )
                : tab === "open"
                  ? t(
                      "admin.reconciliation.empty.nothingToReviewTitle",
                      "Nothing to review"
                    )
                  : t(
                      "admin.reconciliation.empty.noDecisionsTitle",
                      "No decisions yet"
                    )
            }
            description={
              search.trim()
                ? t(
                    "admin.reconciliation.empty.noMatchesDescription",
                    "Try a different sender, tenant, IBAN, or amount."
                  )
                : tab === "open"
                  ? t(
                      "admin.reconciliation.empty.nothingToReviewDescription",
                      "Every imported bank transfer has either auto-matched or been decided."
                    )
                  : t(
                      "admin.reconciliation.empty.noDecisionsDescription",
                      "Confirmed and rejected transfers will appear here as your audit trail."
                    )
            }
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <Table className="min-w-[860px]">
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("admin.reconciliation.transaction", "Transaction")}
                  </TableHead>
                  <TableHead>
                    {t(
                      "admin.reconciliation.suggestedMatch",
                      "Suggested match"
                    )}
                  </TableHead>
                  <TableHead>
                    {tab === "open"
                      ? t(
                          "admin.reconciliation.needsReviewBecause",
                          "Needs review because"
                        )
                      : t("admin.reconciliation.reason", "Reason")}
                  </TableHead>
                  {tab === "open" ? (
                    <TableHead className="text-right">
                      {t("admin.reconciliation.action", "Action")}
                    </TableHead>
                  ) : (
                    <TableHead className="text-right">
                      {t("admin.reconciliation.outcome", "Outcome")}
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.map((row) => {
                  const busy =
                    (confirm.isPending &&
                      confirm.variables?.matchId === row.id) ||
                    (reject.isPending && reject.variables?.matchId === row.id);
                  // COL-508: what one-click Confirm would book. Only once the
                  // tenancy's open months have loaded — before that the
                  // preview would see the suggested invoice alone.
                  const plan =
                    tab === "open" && row.status === "needs_review"
                      ? confirmSplitFor(row)
                      : null;
                  const rowCurrency =
                    row.bank_transactions?.currency ??
                    decisionPayment(row)?.currency ??
                    "EUR";
                  const planIsSimple =
                    !!plan && isSimpleSplit(plan.split, row.payment_id);
                  // Confirm waits for the tenancy's open months: until they
                  // load (or if they fail to), the admin cannot see what the
                  // split will book.
                  const planBlocked =
                    !!plan &&
                    (invoicesLoading ||
                      invoicesFailed ||
                      !!plan.error ||
                      plan.split.applied_cents === 0);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <TransactionCell row={row} />
                      </TableCell>
                      <TableCell>
                        <SuggestedMatchCell row={row} />
                      </TableCell>
                      <TableCell
                        className="text-xs text-muted-foreground max-w-[18rem]"
                        title={row.review_reason ?? undefined}
                      >
                        <span className="line-clamp-3">
                          {isRemainderRow(row)
                            ? t(
                                "admin.reconciliation.split.remainderReason",
                                "Left over from a transfer that already booked {amount}. Assign it to another invoice, or reject it (e.g. refunded)."
                              ).replace(
                                "{amount}",
                                formatCents(
                                  Math.max(
                                    0,
                                    (row.bank_transactions?.amount_cents ?? 0) -
                                      availableCents(row)
                                  ),
                                  rowCurrency
                                )
                              )
                            : row.review_reason || "—"}
                        </span>
                      </TableCell>
                      {tab === "open" ? (
                        <TableCell>
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openAssign(row)}
                              disabled={busy}
                              title={t(
                                "admin.reconciliation.assignTitle",
                                "Assign this transaction to a tenant's open invoice (or override the suggestion)"
                              )}
                            >
                              <SquarePen className="h-4 w-4 mr-1" />{" "}
                              {t("admin.reconciliation.assign", "Assign")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReject(row)}
                              disabled={busy}
                            >
                              <X className="h-4 w-4 mr-1" />{" "}
                              {t("admin.reconciliation.reject", "Reject")}
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => setConfirmTargetId(row.id)}
                              disabled={
                                busy ||
                                !row.payment_id ||
                                !candidateStillOpen(row.payments) ||
                                planBlocked
                              }
                              title={
                                !row.payment_id
                                  ? t(
                                      "admin.reconciliation.noCandidatePaymentTitle",
                                      "No candidate payment attached"
                                    )
                                  : !candidateStillOpen(row.payments)
                                    ? t(
                                        "admin.reconciliation.suggestionSettledTitle",
                                        "The suggested invoice is no longer open — it was settled after this suggestion was made"
                                      )
                                    : invoicesFailed
                                      ? t(
                                          "admin.reconciliation.split.tenancyLoadFailedTitle",
                                          "The tenancy's open months couldn't be loaded — Confirm is disabled until they are"
                                        )
                                      : t(
                                          "admin.reconciliation.confirmTitle",
                                          "Confirm this match"
                                        )
                              }
                            >
                              <Check className="h-4 w-4 mr-1" />{" "}
                              {t("admin.reconciliation.confirm", "Confirm")}
                            </Button>
                          </div>
                          {/* COL-417/COL-508: the tenancy owes Mahngebühren.
                              Left empty, the split rule pays them right after
                              the oldest open rent (the placeholder shows how
                              much); a typed amount is the admin stating the
                              fee portion instead. */}
                          {plan && plan.owed > 0 && (
                            <div className="mt-2 flex items-center justify-end gap-2 text-xs text-muted-foreground">
                              <label htmlFor={`fee-${row.id}`}>
                                {t(
                                  "admin.reconciliation.feePortion",
                                  "of which Mahngebühren (€)"
                                )}
                              </label>
                              <input
                                id={`fee-${row.id}`}
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                className="h-7 w-20 rounded border px-2 text-right"
                                value={feeSplit[row.id] ?? ""}
                                placeholder={
                                  plan.feeOverride == null
                                    ? (plan.split.fee_cents / 100).toFixed(2)
                                    : "0.00"
                                }
                                onChange={(e) =>
                                  setFeeSplit((prev) => ({
                                    ...prev,
                                    [row.id]: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          )}
                          {plan?.error && (
                            <div className="mt-1 text-right text-xs text-rose-600">
                              {feeErrorMessage(
                                plan.error,
                                plan.owed,
                                rowCurrency,
                                t
                              )}
                            </div>
                          )}
                          {/* COL-508: Confirm now splits the transfer across
                              the tenancy — say what it will book whenever that
                              is more than "all of it onto this invoice". */}
                          {/* COL-508: the split preview is what Confirm is
                              gated on, so a failed tenancy lookup disables it —
                              say so, rather than leaving a dead button under a
                              tooltip that reads as if it should work. */}
                          {plan && invoicesFailed && (
                            <div className="mt-1 max-w-[22rem] ml-auto text-right text-xs text-rose-600">
                              {t(
                                "admin.reconciliation.split.tenancyLoadFailed",
                                "Couldn't load this tenancy's open months, so what Confirm would book can't be shown. Retry before confirming."
                              )}
                            </div>
                          )}
                          {plan &&
                            !invoicesLoading &&
                            !invoicesFailed &&
                            !plan.error && (
                              <>
                                {plan.split.applied_cents === 0 ? (
                                  <div className="mt-1 max-w-[22rem] ml-auto text-right text-xs text-rose-600">
                                    {t(
                                      "admin.reconciliation.split.nothingApplicable",
                                      "Nothing on this tenancy can take this transfer — every open month is already being collected by SEPA/card. Assign it to another invoice or reject it."
                                    )}
                                  </div>
                                ) : (
                                  !planIsSimple && (
                                    <div
                                      className="mt-1 max-w-[22rem] ml-auto text-right text-xs text-blue-700"
                                      data-testid={`confirm-split-${row.id}`}
                                    >
                                      {confirmBooksText(
                                        plan.split,
                                        rowCurrency,
                                        t,
                                        language
                                      )}
                                    </div>
                                  )
                                )}
                              </>
                            )}
                        </TableCell>
                      ) : (
                        <TableCell className="text-right">
                          <div className="flex justify-end">
                            <OutcomeCell row={row} />
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog
        open={!!assignTarget}
        onOpenChange={(v) => {
          if (!v) closeAssign();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-petrol/10 text-petrol">
                <SquarePen className="h-5 w-5" />
              </span>
              {t(
                "admin.reconciliation.assignDialog.title",
                "Assign transaction to an invoice"
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.reconciliation.assignDialog.descriptionPrefix",
                "Pick which open invoice the"
              )}{" "}
              <span className="font-medium text-foreground">
                {assignTarget?.bank_transactions
                  ? formatCents(assignAvailableCents, assignCurrency)
                  : ""}
              </span>{" "}
              {/* COL-508: a remainder row assigns only what is left of it. */}
              {assignTarget && isTransferPortion(assignTarget) && (
                <>
                  (
                  {t(
                    "admin.reconciliation.split.leftOfTransfer",
                    "left from a transfer of {total}"
                  ).replace(
                    "{total}",
                    formatCents(
                      assignTarget.bank_transactions?.amount_cents ?? 0,
                      assignCurrency
                    )
                  )}
                  ){" "}
                </>
              )}
              {t(
                "admin.reconciliation.assignDialog.descriptionFrom",
                "transfer from"
              )}{" "}
              <span className="font-medium text-foreground">
                {assignTarget?.bank_transactions?.counterpart_name ||
                  t("admin.reconciliation.thisSender", "this sender")}
              </span>{" "}
              {t(
                "admin.reconciliation.assignDialog.descriptionSuffixSplit",
                "pays. The invoice tells us which tenancy it's for: the transfer then pays that tenancy's oldest open rent first, then its Mahngebühren, then the newer months. You'll see exactly what gets booked before you assign. Every change is recorded for audit."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedInvoice ? (
              // Selected state (Neel #1): once an invoice is picked, the
              // search resets and the current selection is shown explicitly —
              // exactly one invoice can be selected at a time.
              <div
                className="flex items-start justify-between gap-2 rounded-md border border-petrol/40 bg-petrol/5 px-3 py-2"
                data-testid="assign-selected-invoice"
              >
                <div className="text-sm">
                  <div className="text-xs font-medium uppercase tracking-wide text-petrol mb-0.5 flex items-center gap-1">
                    <Check className="h-3.5 w-3.5" />{" "}
                    {t(
                      "admin.reconciliation.selectedInvoice",
                      "Selected invoice"
                    )}
                  </div>
                  <SelectedInvoiceSummary
                    invoice={selectedInvoice}
                    t={t}
                    language={language}
                  />
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setAssignInvoiceId(null);
                    // COL-508 / PR review: the fee portion is stated against
                    // THIS tenancy's Mahngebühren. Carried to a tenancy that
                    // owes none it fails validation as "exceeds_owed" and
                    // disables Assign — while the input and its error are both
                    // hidden (they render only when fees are owed), leaving no
                    // way to clear it but reopening the dialog.
                    setAssignFeeAmount("");
                    resetFeeAdjust();
                  }}
                >
                  {t("admin.reconciliation.change", "Change")}
                </Button>
              </div>
            ) : (
              <>
                <DebouncedSearchInput
                  value={assignSearch}
                  onDebouncedChange={setAssignSearch}
                  placeholder={t(
                    "admin.reconciliation.assignSearchPlaceholder",
                    "Search tenant, property, month, amount…"
                  )}
                />
                <div
                  className="max-h-64 overflow-y-auto rounded-md border divide-y"
                  role="listbox"
                  aria-label={t(
                    "admin.reconciliation.openInvoices",
                    "Open invoices"
                  )}
                >
                  {invoicesLoading ? (
                    <div className="p-3 text-sm text-muted-foreground">
                      {t(
                        "admin.reconciliation.loadingOpenInvoices",
                        "Loading open invoices…"
                      )}
                    </div>
                  ) : assignCandidates.length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground">
                      {t(
                        "admin.reconciliation.noOpenInvoiceMatch",
                        "No open invoice matches this search."
                      )}
                    </div>
                  ) : (
                    assignCandidates.map((inv) => (
                      <button
                        key={inv.id}
                        type="button"
                        role="option"
                        aria-selected={assignInvoiceId === inv.id}
                        onClick={() => {
                          setAssignInvoiceId(inv.id);
                          setAssignSearch("");
                          // See the Change button above.
                          setAssignFeeAmount("");
                          resetFeeAdjust();
                        }}
                        className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-muted/60"
                      >
                        {invoiceLabel(inv, t, language)}
                        {(inv.received_cents ?? 0) > 0 && (
                          <span className="ml-2 text-xs text-blue-700">
                            {t(
                              "admin.reconciliation.amountOpen",
                              "{amount} open"
                            ).replace(
                              "{amount}",
                              formatCents(openBalanceCents(inv), inv.currency)
                            )}
                          </span>
                        )}
                        {inv.status === "overdue" ? (
                          <span className="ml-2 text-xs text-rose-600">
                            {t("admin.reconciliation.overdue", "overdue")}
                          </span>
                        ) : null}
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
            {/* COL-417/COL-508: the tenancy owes Mahngebühren. Left empty,
                the split rule pays them right after the oldest open rent (the
                placeholder shows how much); a typed amount is the admin
                stating the fee portion instead. */}
            {selectedInvoice && !isDepositAnchor && assignFeesOwed > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
                  <label htmlFor="assign-fee">
                    {t(
                      "admin.reconciliation.feePortion",
                      "of which Mahngebühren (€)"
                    )}
                  </label>
                  <input
                    id="assign-fee"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    className="h-7 w-20 rounded border px-2 text-right"
                    value={assignFeeAmount}
                    placeholder={
                      assignFeeOverride == null && assignSplit
                        ? (assignSplit.fee_cents / 100).toFixed(2)
                        : "0.00"
                    }
                    onChange={(e) => setAssignFeeAmount(e.target.value)}
                  />
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  {t(
                    "admin.reconciliation.split.feesOwedHint",
                    "{amount} owed on this tenancy · leave empty for the automatic split"
                  ).replace(
                    "{amount}",
                    formatCents(assignFeesOwed, assignCurrency)
                  )}
                </div>
                {assignFeeError && (
                  <div className="text-right text-xs text-rose-600">
                    {feeErrorMessage(
                      assignFeeError,
                      assignFeesOwed,
                      assignCurrency,
                      t
                    )}
                  </div>
                )}
                {/* COL-637: settle the Mahngebühren for less (or more) than
                    the dunning total — logged with a reason. */}
                {!feeAdjustOpen ? (
                  <div className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      variant="link"
                      className="h-auto p-0 text-xs"
                      onClick={() => setFeeAdjustOpen(true)}
                    >
                      {t(
                        "admin.reconciliation.feeAdjust.open",
                        "Adjust Mahngebühren owed"
                      )}
                    </Button>
                  </div>
                ) : (
                  <div
                    className="space-y-2 rounded-md border border-amber-300 bg-amber-50/60 px-3 py-2"
                    data-testid="assign-fee-adjust"
                  >
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <label htmlFor="assign-fee-after" className="font-medium">
                        {t(
                          "admin.reconciliation.feeAdjust.afterLabel",
                          "Mahngebühren owed after this transfer (€)"
                        )}
                      </label>
                      <input
                        id="assign-fee-after"
                        type="number"
                        min="0"
                        step="0.01"
                        inputMode="decimal"
                        className="h-7 w-24 rounded border bg-background px-2 text-right"
                        value={feeAfterAmount}
                        placeholder={(feesAfterSplitCents / 100).toFixed(2)}
                        onChange={(e) => setFeeAfterAmount(e.target.value)}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t(
                        "admin.reconciliation.feeAdjust.hint",
                        "Without a change the tenant still owes {amount}. Enter 0 to close the Mahngebühren."
                      ).replace(
                        "{amount}",
                        formatCents(feesAfterSplitCents, assignCurrency)
                      )}
                    </div>
                    <Textarea
                      value={feeAdjustReason}
                      onChange={(e) => setFeeAdjustReason(e.target.value)}
                      rows={2}
                      aria-label={t(
                        "admin.reconciliation.feeAdjust.reasonLabel",
                        "Reason for the adjustment (required, kept for audit)"
                      )}
                      placeholder={t(
                        "admin.reconciliation.feeAdjust.reasonPlaceholder",
                        "Reason (required): e.g. agreed with the tenant to settle at 10 €"
                      )}
                    />
                    <div className="flex items-center justify-between gap-2">
                      {feeAdjustInvalid ? (
                        <span className="text-xs text-rose-600">
                          {t(
                            "admin.reconciliation.feeAdjust.required",
                            "Enter the amount owed and a reason, or cancel the adjustment."
                          )}
                        </span>
                      ) : (
                        <span />
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={resetFeeAdjust}
                      >
                        {t(
                          "admin.reconciliation.feeAdjust.cancel",
                          "Cancel adjustment"
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* COL-508: exactly what the transfer will book, month by month —
                the dialog's own description is necessarily generic. */}
            {selectedInvoice && assignSplit && !invoicesLoading && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                data-testid="assign-split-preview"
              >
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t(
                    "admin.reconciliation.split.title",
                    "How this transfer is booked"
                  )}
                </div>
                {assignSplit.applied_cents === 0 ? (
                  <div className="text-xs text-rose-600">
                    {t(
                      "admin.reconciliation.split.nothingApplicable",
                      "Nothing on this tenancy can take this transfer — every open month is already being collected by SEPA/card. Assign it to another invoice or reject it."
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_auto_auto] items-baseline gap-x-2 gap-y-1 text-xs tabular-nums sm:gap-x-3 sm:text-sm">
                    <span />
                    <span className="text-right text-xs text-muted-foreground">
                      {t("admin.reconciliation.split.colOpen", "Open")}
                    </span>
                    <span className="text-right text-xs text-muted-foreground">
                      {t("admin.reconciliation.split.colBooked", "Booked")}
                    </span>
                    <span className="text-right text-xs text-muted-foreground">
                      {t("admin.reconciliation.split.colAfter", "After")}
                    </span>
                    {assignSplit.rent.map((line, i) => (
                      <Fragment key={line.payment_id}>
                        <div className="contents" data-testid="split-row">
                          <span className="min-w-0 hyphens-auto break-words">
                            {t(
                              "admin.reconciliation.split.rentLine",
                              "Rent {month}"
                            ).replace(
                              "{month}",
                              periodLabel(line.due_date, language)
                            )}
                          </span>
                          <span className="text-right text-muted-foreground">
                            {formatCents(
                              line.cents + line.open_after_cents,
                              assignCurrency
                            )}
                          </span>
                          <span className="text-right font-medium">
                            {formatCents(line.cents, assignCurrency)}
                          </span>
                          {line.settles ? (
                            <span className="text-right text-emerald-700">
                              {t(
                                "admin.reconciliation.split.paidInFull",
                                "Paid in full"
                              )}
                            </span>
                          ) : (
                            <span className="text-right text-blue-700">
                              {t(
                                "admin.reconciliation.split.stillOpen",
                                "{amount} still open"
                              ).replace(
                                "{amount}",
                                formatCents(
                                  line.open_after_cents,
                                  assignCurrency
                                )
                              )}
                            </span>
                          )}
                        </div>
                        {i === 0 && assignFeesOwed > 0 && feeRow}
                      </Fragment>
                    ))}
                    {assignSplit.rent.length === 0 &&
                      assignFeesOwed > 0 &&
                      feeRow}
                    {assignSplit.credit_cents > 0 && (
                      <div className="contents" data-testid="split-row">
                        <span className="min-w-0 hyphens-auto break-words">
                          {t(
                            "admin.reconciliation.split.creditLine",
                            "Credit for the next rent"
                          )}
                        </span>
                        <span />
                        <span className="text-right font-medium text-emerald-700">
                          {formatCents(
                            assignSplit.credit_cents,
                            assignCurrency
                          )}
                        </span>
                        <span />
                      </div>
                    )}
                    {assignSplit.remainder_cents > 0 && (
                      <div className="contents" data-testid="split-row">
                        <span className="min-w-0 hyphens-auto break-words">
                          {t(
                            "admin.reconciliation.split.remainderLine",
                            "Stays in the Open queue"
                          )}
                        </span>
                        <span />
                        <span className="text-right font-medium">
                          {formatCents(
                            assignSplit.remainder_cents,
                            assignCurrency
                          )}
                        </span>
                        <span />
                      </div>
                    )}
                  </div>
                )}
                {assignSplit.skipped_in_flight.map((m) => (
                  <div
                    key={m.payment_id}
                    className="mt-1 text-xs text-muted-foreground"
                  >
                    {t(
                      "admin.reconciliation.split.skippedInFlight",
                      "{month} skipped — a SEPA/card payment is already collecting it."
                    ).replace("{month}", periodLabel(m.due_date, language))}
                  </div>
                ))}
              </div>
            )}
            {/* Deposit: one fixed invoice, no cascade. Book up to its open
                balance; any overpayment stays in the queue as its own row. */}
            {isDepositAnchor && selectedInvoice && !invoicesLoading && (
              <div
                className="rounded-md border px-3 py-2 text-sm"
                data-testid="assign-deposit-preview"
              >
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t(
                    "admin.reconciliation.split.title",
                    "How this transfer is booked"
                  )}
                </div>
                {depositTakeCents === 0 ? (
                  <div className="text-xs text-rose-600">
                    {t(
                      "admin.reconciliation.split.depositNothing",
                      "This deposit has no open balance left to apply. Assign the transfer to another invoice or reject it."
                    )}
                  </div>
                ) : (
                  <ul className="space-y-1">
                    <li className="flex items-baseline justify-between gap-3">
                      <span>
                        {t("admin.tenants.securityDeposit", "Security deposit")}
                      </span>
                      <span className="text-right tabular-nums text-emerald-700">
                        {formatCents(depositTakeCents, assignCurrency)} ·{" "}
                        {t("admin.reconciliation.split.paid", "paid")}
                      </span>
                    </li>
                    {depositRemainderCents > 0 && (
                      <li className="flex items-baseline justify-between gap-3">
                        <span>
                          {t(
                            "admin.reconciliation.split.remainderLine",
                            "Stays in the Open queue"
                          )}
                        </span>
                        <span className="text-right tabular-nums">
                          {formatCents(depositRemainderCents, assignCurrency)}
                        </span>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
            {/* COL-508: what is left once every open month is paid. Ticked,
                it is credit on the next rent — booked the moment that invoice
                is issued. Unticked, it stays in the queue as its own row. */}
            {selectedInvoice &&
              assignSplit &&
              assignLeftoverCents > 0 &&
              assignSplit.rent.length > 0 && (
                <div className="flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2">
                  <Checkbox
                    id="assign-credit-next"
                    checked={assignCreditNext}
                    onCheckedChange={(v) => setAssignCreditNext(v === true)}
                    className="mt-0.5"
                  />
                  <div className="space-y-0.5">
                    <label
                      htmlFor="assign-credit-next"
                      className="text-sm font-medium leading-snug"
                    >
                      {t(
                        "admin.reconciliation.split.creditNext",
                        "Apply the remaining {amount} as credit to the next invoice"
                      ).replace(
                        "{amount}",
                        formatCents(assignLeftoverCents, assignCurrency)
                      )}
                    </label>
                    <p className="text-xs text-muted-foreground">
                      {assignCreditNext
                        ? t(
                            "admin.reconciliation.split.creditNextOnHint",
                            "It's booked onto this tenancy's next rent invoice as soon as that invoice is issued."
                          )
                        : t(
                            "admin.reconciliation.split.creditNextOffHint",
                            "The {amount} stays in the Open queue so you can assign it to another invoice or reject it (e.g. refunded)."
                          ).replace(
                            "{amount}",
                            formatCents(assignLeftoverCents, assignCurrency)
                          )}
                    </p>
                  </div>
                </div>
              )}
            <div className="space-y-1.5">
              <Label htmlFor="assign-note">
                {t(
                  "admin.reconciliation.noteOptional",
                  "Note (optional, kept for audit)"
                )}
              </Label>
              <Textarea
                id="assign-note"
                value={assignNote}
                onChange={(e) => setAssignNote(e.target.value)}
                rows={2}
                placeholder={t(
                  "admin.reconciliation.assignNotePlaceholder",
                  "e.g. tenant paid from a family member's account…"
                )}
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={closeAssign}>
              {t("admin.reconciliation.cancel", "Cancel")}
            </Button>
            <Button
              onClick={submitAssign}
              disabled={
                !assignInvoiceId ||
                assign.isPending ||
                !!assignFeeError ||
                feeAdjustInvalid ||
                // COL-508: never submit a split the admin has not been shown.
                // `assignInvoiceId` is preselected the moment the dialog opens,
                // from the match's own joined payment — independently of the
                // open-invoice query. While that query is loading or failed
                // `selectedInvoice`, and so `assignSplit`, are null, and a
                // `!!assignSplit && …` guard is vacuously false: the button
                // stayed enabled and booked a multi-month split blind. Confirm
                // already fails closed on the same data; this now matches it.
                // A deposit books the single invoice (no split); it is ready
                // once there is an open balance to take.
                (isDepositAnchor
                  ? depositTakeCents === 0
                  : !assignSplit || assignSplit.applied_cents === 0)
              }
            >
              <Check className="h-4 w-4 mr-1" />{" "}
              {assignAvailableCents > 0
                ? t(
                    "admin.reconciliation.assignAmount",
                    "Assign {amount}"
                  ).replace(
                    "{amount}",
                    formatCents(assignAvailableCents, assignCurrency)
                  )
                : t(
                    "admin.reconciliation.assignAndMarkPaid",
                    "Assign transfer"
                  )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmAssignmentDialog
        row={confirmRow}
        split={confirmPlan?.split ?? null}
        blockedReason={confirmBlockedReason}
        blocked={!!confirmBlockedReason || invoicesLoading}
        pending={confirm.isPending}
        onCancel={() => setConfirmTargetId(null)}
        onConfirm={submitConfirm}
      />

      <Dialog
        open={!!rejectTarget}
        onOpenChange={(v) => {
          if (!v) {
            setRejectTarget(null);
            setReasonDraft("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-rose-600">
                <AlertTriangle className="h-5 w-5" />
              </span>
              {t(
                "admin.reconciliation.rejectDialog.title",
                "Reject reconciliation match"
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "admin.reconciliation.rejectDialog.descriptionPrefix",
                "This dismisses the suggested match for"
              )}{" "}
              <span className="font-medium text-foreground">
                {rejectTarget?.bank_transactions?.counterpart_name ||
                  t("admin.reconciliation.thisTransaction", "this transaction")}
              </span>
              .{" "}
              {t(
                "admin.reconciliation.rejectDialog.descriptionSuffix",
                "Give a reason — it's recorded for audit."
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">
              {t("admin.reconciliation.reason", "Reason")}{" "}
              <span className="text-rose-500">*</span>
            </Label>
            <Textarea
              id="reject-reason"
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              rows={4}
              autoFocus
              placeholder={t(
                "admin.reconciliation.rejectReasonPlaceholder",
                "e.g. Wrong tenant suggested / amount doesn't match / already reconciled elsewhere…"
              )}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setReasonDraft("");
              }}
            >
              {t("admin.reconciliation.cancel", "Cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={!reasonDraft.trim() || reject.isPending}
            >
              {t("admin.reconciliation.rejectMatch", "Reject match")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminV2Layout>
  );
}

/** COL-637: the picked invoice as month + tenancy, with what was invoiced,
 *  what already came in and what is still open — the open balance is what the
 *  transfer actually pays, so it is the number that must stand out. */
function SelectedInvoiceSummary({
  invoice,
  t,
  language,
}: {
  invoice: OpenInvoice;
  t: Translate;
  language: string;
}) {
  const { tenant, where, kind } = invoiceParts(invoice, t, language);
  const received = Math.max(0, invoice.received_cents ?? 0);
  const open = openBalanceCents(invoice);
  const paidPct =
    invoice.amount_cents > 0
      ? Math.min(100, Math.round((received / invoice.amount_cents) * 100))
      : 0;
  const cell = "min-w-0 rounded-md bg-background px-2 py-1.5";
  return (
    <div className="space-y-2">
      <div>
        <div className="font-medium">{kind}</div>
        <div className="text-xs text-muted-foreground">
          {tenant} · {where}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-xs tabular-nums sm:gap-2 sm:text-sm">
        <div className={cell}>
          <div className="text-xs text-muted-foreground">
            {t("admin.reconciliation.invoiceTotal", "Invoice")}
          </div>
          <div className="font-medium">
            {formatCents(invoice.amount_cents, invoice.currency)}
          </div>
        </div>
        <div className={cell}>
          <div className="text-xs text-muted-foreground">
            {t("admin.reconciliation.alreadyPaid", "Already paid")}
          </div>
          <div className="font-medium text-muted-foreground">
            {formatCents(received, invoice.currency)}
          </div>
        </div>
        <div className={`${cell} border border-blue-300`}>
          <div className="text-xs text-blue-700">
            {t("admin.reconciliation.stillOpenLabel", "Still open")}
          </div>
          <div
            className="font-medium text-blue-700"
            data-testid="assign-selected-open"
          >
            {formatCents(open, invoice.currency)}
          </div>
        </div>
      </div>
      {received > 0 && (
        <div
          className="h-1.5 overflow-hidden rounded-full bg-blue-100"
          aria-hidden="true"
        >
          <div
            className="h-full bg-emerald-600"
            style={{ width: `${paidPct}%` }}
          />
        </div>
      )}
    </div>
  );
}
